import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select,
  Stack, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { emptyTask, localYmd, MOMENTS, WEEKDAYS } from "../taskUi";
import {
  compactFieldSx, primaryActionSx, secondaryActionSx, taskDialogPaperSx,
} from "./TaskVisualShell";

const initialForm = () => ({
  roleId: "", name: "", moment: "DURING_SHIFT", recurrence: "EVERY_WORKDAY",
  weekdays: [], validFrom: localYmd(), validUntil: "", tasks: [emptyTask()],
});

export default function RoutineEditorDialog({ open, routine, roles, saving, onClose, onSave }) {
  const keyRef = useRef(1);
  const titleRefs = useRef({});
  const stepRefs = useRef({});
  const addKeys = (tasks) => tasks.map((task) => ({
    ...task,
    _key: task._key ?? (task.id ? `task-${task.id}` : `task-new-${keyRef.current++}`),
    steps: (task.steps ?? []).map((step) => ({
      ...step,
      _key: step._key ?? (step.id ? `step-${step.id}` : `step-new-${keyRef.current++}`),
    })),
  }));
  const [form, setForm] = useState(initialForm);
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (!open) return;
    const nextForm = routine ? {
      roleId: String(routine.roleId), name: routine.name, moment: routine.moment,
      recurrence: routine.recurrence, weekdays: routine.weekdays ?? [],
      validFrom: String(routine.validFrom).slice(0, 10), validUntil: routine.validUntil ? String(routine.validUntil).slice(0, 10) : "",
      tasks: addKeys(routine.tasks?.length ? routine.tasks : [emptyTask()]),
    } : { ...initialForm(), tasks: addKeys([emptyTask()]) };
    setForm(nextForm);
    setAttempted(false);
  }, [open, routine]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const updateTask = (index, patch) => setForm((current) => ({
    ...current, tasks: current.tasks.map((task, taskIndex) => taskIndex === index ? { ...task, ...patch } : task),
  }));
  const moveTask = (index, direction) => setForm((current) => {
    const tasks = [...current.tasks];
    const target = index + direction;
    if (target < 0 || target >= tasks.length) return current;
    [tasks[index], tasks[target]] = [tasks[target], tasks[index]];
    return { ...current, tasks };
  });
  const updateStep = (taskIndex, stepIndex, title) => updateTask(taskIndex, {
    steps: form.tasks[taskIndex].steps.map((step, index) => index === stepIndex ? { ...step, title } : step),
  });
  const addTask = () => {
    const [task] = addKeys([emptyTask()]);
    setForm((current) => ({ ...current, tasks: [...current.tasks, task] }));
    window.setTimeout(() => titleRefs.current[task._key]?.focus(), 0);
  };
  const addStep = (taskIndex) => {
    const step = { title: "", _key: `step-new-${keyRef.current++}` };
    updateTask(taskIndex, { steps: [...form.tasks[taskIndex].steps, step] });
  };
  const removeTask = (taskIndex) => {
    setForm((current) => ({ ...current, tasks: current.tasks.filter((_, index) => index !== taskIndex) }));
  };

  const valid = Boolean(form.roleId && form.name.trim() && form.validFrom && form.tasks.length &&
    form.tasks.every((task) => task.title.trim() && task.steps.every((step) => step.title.trim())) &&
    (form.recurrence === "EVERY_WORKDAY" || form.weekdays.length > 0) &&
    (!form.validUntil || form.validUntil >= form.validFrom));
  const invalidTaskKeys = useMemo(() => new Set(form.tasks
    .filter((task) => !task.title.trim() || task.steps.some((step) => !step.title.trim()))
    .map((task) => task._key)), [form.tasks]);
  const submit = () => {
    setAttempted(true);
    if (!valid) {
      const invalidTask = form.tasks.find((task) => invalidTaskKeys.has(task._key));
      if (invalidTask) {
        window.setTimeout(() => {
          if (!invalidTask.title.trim()) titleRefs.current[invalidTask._key]?.focus();
          else {
            const step = invalidTask.steps.find((item) => !item.title.trim());
            stepRefs.current[`${invalidTask._key}:${step?._key}`]?.focus();
          }
        }, 0);
      }
      return;
    }
    onSave(form);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" PaperProps={{ sx: taskDialogPaperSx }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: "primary.800" }}>{routine ? "Editar rutina" : "Nueva rutina"}</Typography>
        <IconButton size="small" onClick={onClose} disabled={saving} aria-label="Cerrar formulario"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: { xs: 2, md: 2.5 }, py: 2 }}>
        <Stack spacing={2}>
          {attempted && !valid ? <Alert severity="error">Revisá los campos señalados antes de guardar.</Alert> : null}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <FormControl fullWidth required size="small" error={attempted && !form.roleId} sx={compactFieldSx}>
              <InputLabel id="routine-role-label">Rol</InputLabel>
              <Select labelId="routine-role-label" label="Rol" value={form.roleId} onChange={(event) => update("roleId", event.target.value)}>
                {roles.map((role) => <MenuItem key={role.id} value={String(role.id)}>{role.name}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField required size="small" label="Nombre de la rutina" value={form.name} onChange={(event) => update("name", event.target.value)} error={attempted && !form.name.trim()} sx={compactFieldSx} />
            <FormControl fullWidth size="small" sx={compactFieldSx}>
              <InputLabel id="routine-moment-label">Momento</InputLabel>
              <Select labelId="routine-moment-label" label="Momento" value={form.moment} onChange={(event) => update("moment", event.target.value)}>
                {MOMENTS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" error={attempted && form.recurrence === "SPECIFIC_DAYS" && !form.weekdays.length} sx={compactFieldSx}>
              <InputLabel id="routine-repeat-label">Repetición</InputLabel>
              <Select labelId="routine-repeat-label" label="Repetición" value={form.recurrence} onChange={(event) => update("recurrence", event.target.value)}>
                <MenuItem value="EVERY_WORKDAY">Cada día que trabaje</MenuItem>
                <MenuItem value="SPECIFIC_DAYS">Días específicos</MenuItem>
              </Select>
            </FormControl>
            <TextField required size="small" type="date" label="Vigente desde" InputLabelProps={{ shrink: true }} value={form.validFrom} onChange={(event) => update("validFrom", event.target.value)} error={attempted && !form.validFrom} sx={compactFieldSx} />
            <TextField size="small" type="date" label="Vigente hasta (opcional)" InputLabelProps={{ shrink: true }} value={form.validUntil} onChange={(event) => update("validUntil", event.target.value)} error={attempted && Boolean(form.validUntil && form.validUntil < form.validFrom)} sx={compactFieldSx} />
          </Box>
          {form.recurrence === "SPECIFIC_DAYS" ? (
            <Box sx={{ px: 1.5, py: 1, bgcolor: "neutral.50", border: "1px solid", borderColor: attempted && !form.weekdays.length ? "error.main" : "neutral.200", borderRadius: "12px" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "primary.900" }}>Días en que se repite</Typography>
              <Stack direction="row" flexWrap="wrap" columnGap={0.5}>
                {WEEKDAYS.map((day) => (
                  <FormControlLabel key={day.value} label={day.label} sx={{ mr: 1 }} control={<Checkbox size="small" checked={form.weekdays.includes(day.value)} onChange={(event) => update("weekdays", event.target.checked ? [...form.weekdays, day.value] : form.weekdays.filter((value) => value !== day.value))} />} />
                ))}
              </Stack>
            </Box>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} gap={1} justifyContent="space-between" alignItems={{ sm: "center" }}>
            <Box><Typography sx={{ fontSize: 18, fontWeight: 800, color: "primary.900" }}>Tareas</Typography><Typography sx={{ fontSize: 13, color: "neutral.700" }}>Cargá títulos y pasos directamente, en el orden de trabajo.</Typography></Box>
            <Button startIcon={<AddIcon />} onClick={addTask} sx={{ textTransform: "none", fontWeight: 700, alignSelf: { xs: "flex-start", sm: "center" } }}>Añadir tarea</Button>
          </Stack>

          {form.tasks.map((task, taskIndex) => (
            <Box key={task._key ?? `initial-task-${taskIndex}`} sx={{ bgcolor: "neutral.50", border: "1px solid", borderColor: attempted && invalidTaskKeys.has(task._key) ? "error.main" : "neutral.200", borderRadius: "14px", overflow: "hidden" }}>
              <Stack direction="row" alignItems="center" gap={0.5} sx={{ px: 1.5, py: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: "primary.900" }}>Tarea {taskIndex + 1} · {task.title.trim() || "Nueva tarea"}</Typography>
                    {attempted && invalidTaskKeys.has(task._key) ? <Chip label="Revisar campos" size="small" color="error" /> : null}
                  </Stack>
                  <Typography sx={{ fontSize: 12, color: "neutral.700" }}>{task.steps.length ? `${task.steps.length} ${task.steps.length === 1 ? "paso" : "pasos"}` : "Sin checklist"}{task.estimatedMinutes ? ` · ${task.estimatedMinutes} min` : ""}</Typography>
                </Box>
                <Stack direction="row" flexShrink={0}>
                  <IconButton size="small" aria-label={`Subir tarea ${taskIndex + 1}`} disabled={!taskIndex} onClick={() => moveTask(taskIndex, -1)}><KeyboardArrowUpIcon fontSize="small" /></IconButton>
                  <IconButton size="small" aria-label={`Bajar tarea ${taskIndex + 1}`} disabled={taskIndex === form.tasks.length - 1} onClick={() => moveTask(taskIndex, 1)}><KeyboardArrowDownIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" aria-label={`Quitar tarea ${taskIndex + 1}`} disabled={form.tasks.length === 1} onClick={() => removeTask(taskIndex)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </Stack>
              </Stack>
              <Divider />
              <Stack spacing={1.25} sx={{ p: 1.5 }}>
                  <TextField required size="small" label="Título de la tarea" value={task.title} inputRef={(node) => { titleRefs.current[task._key] = node; }} onChange={(event) => updateTask(taskIndex, { title: event.target.value })} error={attempted && !task.title.trim()} sx={compactFieldSx} />
                  <TextField size="small" multiline minRows={1} maxRows={3} label="Descripción (opcional)" value={task.description ?? ""} onChange={(event) => updateTask(taskIndex, { description: event.target.value })} sx={compactFieldSx} />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.25 }}>
                    <TextField size="small" type="number" inputProps={{ min: 1 }} label="Duración estimada (min)" value={task.estimatedMinutes ?? ""} onChange={(event) => updateTask(taskIndex, { estimatedMinutes: event.target.value })} sx={compactFieldSx} />
                    <TextField size="small" type="time" label="Hora objetivo (opcional)" InputLabelProps={{ shrink: true }} value={task.targetTime ? String(task.targetTime).slice(0, 5) : ""} onChange={(event) => updateTask(taskIndex, { targetTime: event.target.value })} sx={compactFieldSx} />
                  </Box>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography sx={{ fontSize: 13, fontWeight: 800, color: "primary.900" }}>Checklist</Typography><Button size="small" onClick={() => addStep(taskIndex)} sx={{ textTransform: "none", fontWeight: 700 }}>Añadir paso</Button></Stack>
                    {task.steps.map((step, stepIndex) => (
                    <Stack key={step._key} direction="row" gap={0.75} mt={0.75} alignItems="center">
                      <TextField fullWidth size="small" label={`Paso ${stepIndex + 1}`} value={step.title} inputRef={(node) => { stepRefs.current[`${task._key}:${step._key}`] = node; }} onChange={(event) => updateStep(taskIndex, stepIndex, event.target.value)} error={attempted && !step.title.trim()} sx={compactFieldSx} />
                      <IconButton size="small" color="error" aria-label={`Quitar paso ${stepIndex + 1}`} onClick={() => updateTask(taskIndex, { steps: task.steps.filter((_, index) => index !== stepIndex) })}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </Stack>
                  ))}
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: "center", gap: 1 }}>
        <Button variant="contained" disabled={saving} onClick={submit} sx={primaryActionSx}>{saving ? "Guardando…" : "Guardar rutina"}</Button>
        <Button variant="outlined" onClick={onClose} disabled={saving} sx={secondaryActionSx}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
}
